import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { addMonths, addWeeks, addYears, format } from "date-fns";

export interface RecurringSeries {
  id: string;
  description: string;
  type: "income" | "expense";
  amount: number;
  frequency: "weekly" | "monthly" | "yearly";
  totalInstallments?: number;
  paidInstallments: number;
  nextDueDate: string;
  isActive: boolean;
  categoryName?: string;
  categoryColor?: string;
  accountName?: string;
  budgetItemName?: string;
  transactions: RecurringTransaction[];
}

export interface RecurringTransaction {
  id: string;
  description: string;
  amount: number;
  transaction_date: string;
  is_paid: boolean;
  installment_number?: number;
  total_installments?: number;
}

export interface CreateRecurringParams {
  description: string;
  type: "income" | "expense";
  totalAmount: number;
  categoryId: string;
  accountId: string;
  startDate: string;
  recurrenceType: "installment" | "fixed";
  installments?: number;
  frequency: "weekly" | "monthly" | "yearly";
  endDate?: string;
}

export const useRecurringTransactions = () => {
  const [series, setSeries] = useState<RecurringSeries[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRecurringSeries = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Single RPC call replaces N+1 query pattern
      const { data, error } = await supabase.rpc("get_recurring_series");

      if (error) throw error;

      const rpcData = (data as any[]) || [];
      const seriesData: RecurringSeries[] = rpcData.map((rec: any) => ({
        id: rec.id,
        description: rec.description,
        type: rec.type,
        amount: Number(rec.amount),
        frequency: rec.frequency || "monthly",
        totalInstallments: rec.totalInstallments || undefined,
        paidInstallments: rec.paidInstallments || 0,
        nextDueDate: rec.nextDueDate,
        isActive: rec.isActive,
        categoryName: rec.categoryName,
        categoryColor: rec.categoryColor,
        accountName: rec.accountName,
        budgetItemName: rec.budgetItemName || undefined,
        transactions: (rec.transactions || []).map((t: any, idx: number) => ({
          id: t.id,
          description: t.description,
          amount: Number(t.amount),
          transaction_date: t.transaction_date,
          is_paid: t.is_paid,
          installment_number: idx + 1,
          total_installments: rec.totalInstallments || undefined,
        })),
      }));

      setSeries(seriesData);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar recorrências",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const createRecurringSeries = async (params: CreateRecurringParams) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const {
        description,
        type,
        totalAmount,
        categoryId,
        accountId,
        startDate,
        recurrenceType,
        installments = 1,
        frequency,
        endDate,
      } = params;

      const installmentAmount = recurrenceType === "installment" 
        ? totalAmount / installments 
        : totalAmount;

      const numTransactions = recurrenceType === "installment" ? installments : 12;

      // Create all transactions in batch
      const transactions = [];
      let currentDate = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < numTransactions; i++) {
        const transactionDate = new Date(currentDate);
        transactionDate.setHours(0, 0, 0, 0);

        const isPaid = transactionDate <= today;

        const descWithInstallment = recurrenceType === "installment"
          ? `${description} (${i + 1}/${installments})`
          : description;

        transactions.push({
          user_id: user.id,
          account_id: accountId,
          category_id: categoryId || null,
          amount: installmentAmount,
          type,
          description: descWithInstallment,
          transaction_date: format(currentDate, "yyyy-MM-dd"),
          is_paid: isPaid,
          is_recurring: true,
          is_fixed: recurrenceType === "fixed",
        });

        // Calculate next date
        if (frequency === "weekly") {
          currentDate = addWeeks(currentDate, 1);
        } else if (frequency === "monthly") {
          currentDate = addMonths(currentDate, 1);
        } else {
          currentDate = addYears(currentDate, 1);
        }

        // Break if past end date for fixed recurrences
        if (recurrenceType === "fixed" && endDate && currentDate > new Date(endDate)) {
          break;
        }
      }

      const { data: insertedTransactions, error: txError } = await supabase
        .from("transactions")
        .insert(transactions)
        .select();

      if (txError) throw txError;

      // Create recurrence record linked to first transaction
      const firstTransaction = insertedTransactions?.[0];
      if (firstTransaction) {
        const { error: recError } = await supabase.from("recurrences").insert({
          user_id: user.id,
          transaction_id: firstTransaction.id,
          frequency,
          interval: 1,
          start_date: startDate,
          end_date: endDate || null,
          occurrences: recurrenceType === "installment" ? installments : null,
        });

        if (recError) throw recError;
      }

      toast({
        title: "Transação recorrente criada!",
        description: `${transactions.length} lançamentos gerados com sucesso.`,
      });

      await fetchRecurringSeries();
      return true;
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao criar recorrência",
        description: error.message,
      });
      return false;
    }
  };

  const markAsPaid = async (transactionId: string) => {
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ is_paid: true })
        .eq("id", transactionId);

      if (error) throw error;

      toast({ title: "Transação marcada como paga!" });
      await fetchRecurringSeries();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar transação",
        description: error.message,
      });
    }
  };

  const deleteSeries = async (recurrenceId: string) => {
    try {
      const seriesItem = series.find(s => s.id === recurrenceId);
      if (!seriesItem) return;

      // Delete unpaid transactions
      const unpaidIds = seriesItem.transactions
        .filter(t => !t.is_paid)
        .map(t => t.id);

      if (unpaidIds.length > 0) {
        const { error: txError } = await supabase
          .from("transactions")
          .delete()
          .in("id", unpaidIds);

        if (txError) throw txError;
      }

      // Delete recurrence record
      const { error: recError } = await supabase
        .from("recurrences")
        .delete()
        .eq("id", recurrenceId);

      if (recError) throw recError;

      toast({ title: "Série encerrada com sucesso!" });
      await fetchRecurringSeries();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao encerrar série",
        description: error.message,
      });
    }
  };

  useEffect(() => {
    fetchRecurringSeries();
  }, []);

  return {
    series,
    loading,
    createRecurringSeries,
    markAsPaid,
    deleteSeries,
    refetch: fetchRecurringSeries,
  };
};
